import { useRef, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { CustomProductImage, CustomProductRevision } from '../../../types/custom';
import { getApiErrorDetails } from '../../../utils/apiError';
import { latestCustomProductRevision } from '../../../utils/customProductRevisions';
import { customErrorMessage, newIdempotencyKey } from '../../../utils/customUi';

interface CopyRequest {
  version: string;
  productRevision: number;
  idempotencyKey: string;
}

function savedRequest(storageKey: string): CopyRequest | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null') as CopyRequest | null;
    if (value && typeof value.version === 'string' && Number.isInteger(value.productRevision)
      && value.productRevision > 0 && typeof value.idempotencyKey === 'string') return value;
  } catch { /* A browser may deny session storage; retries still work in this form. */ }
  return null;
}

function storeRequest(storageKey: string, request: CopyRequest | null): void {
  try {
    if (request) sessionStorage.setItem(storageKey, JSON.stringify(request));
    else sessionStorage.removeItem(storageKey);
  } catch { /* The in-memory request remains stable if storage is unavailable. */ }
}

export function CopyCustomImageForm({ source, revisions, images, onClose, onCreated, onSubmittingChange }: {
  source: CustomProductImage;
  revisions: CustomProductRevision[];
  images: CustomProductImage[];
  onClose: () => void;
  onCreated: (image: CustomProductImage) => void;
  onSubmittingChange: (submitting: boolean) => void;
}) {
  const storageKey = `custom-image-copy:${source.productId}:${source.id}`;
  const [request, setRequest] = useState<CopyRequest | null>(() => savedRequest(storageKey));
  const [version, setVersion] = useState(() => request?.version || '');
  const [revision, setRevision] = useState(() => request?.productRevision || latestCustomProductRevision(revisions));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const trimmedVersion = version.trim();
  const duplicate = !request && images.some(image => image.version === trimmedVersion);
  const versionValid = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,63}$/.test(trimmedVersion);

  const submit = async () => {
    if (inFlight.current || (!request && (!versionValid || duplicate || !revision))) return;
    inFlight.current = true;
    setSubmitting(true);
    onSubmittingChange(true);
    setError('');
    const intent = request || { version: trimmedVersion, productRevision: revision, idempotencyKey: newIdempotencyKey('image-copy') };
    setRequest(intent);
    storeRequest(storageKey, intent);
    try {
      const result = await customAdminApi.copyImage(source.productId, source.id, intent);
      storeRequest(storageKey, null);
      onCreated(result.image);
    } catch (caught) {
      const details = getApiErrorDetails(caught, 'The copy request could not be confirmed.');
      // A network/proxy failure may follow a committed intent. Never issue a new key for it.
      const rejected = details.status !== undefined && details.status >= 400 && details.status < 500
        && ![408, 429].includes(details.status);
      if (rejected) {
        setRequest(null);
        storeRequest(storageKey, null);
      }
      setError(customErrorMessage(caught, 'The copy request could not be confirmed.'));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      onSubmittingChange(false);
    }
  };

  return <form className="m-4 p-4 rounded-lg border border-cyber-500/30 bg-cyber-500/5" aria-label={`Create image from ${source.version}`} onSubmit={event => { event.preventDefault(); void submit(); }}>
    <h4 className="font-medium text-gray-200 flex items-center gap-2"><Copy className="w-4 h-4 text-cyber-400" /> Creează din același ZIP</h4>
    <p className="text-xs text-gray-400 mt-2">Reuse the ZIP from <strong>{source.version}</strong> without uploading it again. The new release gets an independent copy, so deleting the original will not affect it. The ZIP is validated against the selected revision before Build becomes available.</p>
    <div className="grid sm:grid-cols-2 gap-3 mt-4">
      <label className="text-xs text-gray-400">New version<input className="input mt-1" value={version} placeholder={`${source.version}-r${revision}`} maxLength={64} autoFocus required disabled={Boolean(request) || submitting} onChange={event => setVersion(event.target.value)} /></label>
      <label className="text-xs text-gray-400">New product revision<select className="input mt-1" value={revision} disabled={Boolean(request) || submitting} onChange={event => setRevision(Number(event.target.value))}>{revisions.map(item => <option key={item.id} value={item.revision}>r{item.revision} · {item.contentHash.slice(0, 10)}</option>)}</select></label>
    </div>
    {duplicate && <p role="alert" className="text-xs text-red-300 mt-2">That version already exists. Choose a new version.</p>}
    {trimmedVersion && !versionValid && <p role="alert" className="text-xs text-red-300 mt-2">Use up to 64 letters, numbers, dots, hyphens or underscores, starting with a letter, number or underscore.</p>}
    {error && <p role="alert" className="text-sm text-red-300 mt-3">{error}</p>}
    {request && !submitting && <p role="status" className="text-xs text-amber-300 mt-3">The previous request may already have been accepted. Retry to recover the same release with the same version and revision.</p>}
    <div className="flex flex-wrap justify-end gap-2 mt-4">
      <button type="button" className="btn-secondary" disabled={submitting} onClick={onClose}>{request ? 'Close' : 'Cancel'}</button>
      <button type="submit" className="btn-primary" disabled={submitting || (!request && (!versionValid || duplicate || !revision))}>{submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}{submitting ? 'Requesting copy…' : request ? 'Retry same request' : 'Create image'}</button>
    </div>
  </form>;
}
